import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  bagSpecs,
  BagsEligibility,
  BagsPricing,
  BagsSpecsRequest,
  Booking,
  BookingAvailability,
  BookingContext,
  CancelBookingOption,
  ChangeServiceConfirmResult,
  ChangeServiceOption,
  ChangeServicePricing,
  ConfirmPaymentResult,
  FlightSearchResult,
  Issue,
  ManageBagsRequest,
  ManageBagsResponse,
  PayBookingResult,
  paymentMethod,
  SecurityCheckAnswer,
  SecurityCheckQuestions,
  TimeSlotsRequest,
  TimeSlotsResponse,
  SlotWindowsResponse,
  TimeWindow,
} from './booking.types';
import { BookingsListResponse } from './booking-list-container/booking-list-container.component';
import { LiveTrackingConnectionResult } from '@app/@shared/booking-details-base/booking-details-base.component';
import { statusesList } from './statuses';
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import { environment } from '@env/environment';
import { CredentialsService } from '@core/services/credentials.service';
import { map, tap } from 'rxjs/operators';

const routes = {
  getBookingDetails: (id: number) => `/org/sgs/booking/${id}`,
  cancelBooking: (id: number) => `/org/sgs/booking/${id}/cancel`,
  getBookings: (c: BookingContext) =>
    `/org/sgs/booking?StatusGroupId=${c.statusGroupId}&PageNumber=${
      c.pageNumber || ''
    }&PageSize=${c.pageSize || ''}`,
  getFlights: (code: string, date: string) => {
    if (date !== '') {
      return `/org/sgs/flight?Code=${code}&Departure=${new Date(
        date,
      ).getTime()}`;
    }
    return `/org/sgs/flight?Code=${code}`;
  },
  timeSlots: () => '/org/sgs/booking/time-slots',
  createPreBooking: () => '/org/sgs/booking/prebookings',
  getBookingPrices: (id: number) => `/org/sgs/booking/${id}/payment`,
  computeBagsSpecs: () => '/org/sgs/pricing/bags-specs',
  computeBagsPricing: () => '/org/sgs/pricing/bags-pricing',
  createBooking: () => '/org/sgs/booking?includeErrorDetails=true',
  createBookingDropoffWindow: (id: number) =>
    `/org/sgs/booking/${id}/dropoffwindows/v2`,
  updateBookingDropoffWindow: (id: number) =>
    `/org/sgs/booking/${id}/dropoffwindow`,
  getSecurityCheck: () => '/org/sgs/security/security-check',
  storeSecurityCheck: () => '/org/sgs/security/security-check',
  getBookingService: (id: number) => `/org/sgs/booking/${id}/service-options`,
  changeBookingService: (id: number) => `/org/sgs/booking/${id}/change-service`,
  createBookingPickupWindow: (id: number) =>
    `/org/sgs/booking/${id}/pickupwindows/v2`,
  updateBookingPickupWindow: (id: number) =>
    `/org/sgs/booking/${id}/pickupwindow`,
  getCancelBookingOptions: (id: number) =>
    `/org/sgs/booking/${id}/cancellation-options`,
  getLiveTrackingConnectionBuilderBaseUrl: (baseUrl: string) =>
    `${baseUrl}/customer/customers-hub`,
  initiateClickToCall: (id: number) => `/org/sgs/booking/${id}/calls`,
  generateBagImageUrl: () => '/org/sgs/configuration/storage-config',
  getBookingAvailability: () => '/org/sgs/configuration/booking-availability',
  getListOfFeedbackIssues: (id: number) => `/org/sgs/booking/${id}/issues`,
  uploadBagImg: () => `/org/sgs/attachments/add`,
  postBookingFeedback: (id: number) => `/org/sgs/booking/${id}/rating`,
  confirmBooking: (id: number) => `/org/sgs/booking/${id}/confirm`,
  confirmChangeService: (id: number) =>
    `/org/sgs/booking/${id}/confirm-change-service`,
  confirmPayment: (id: number) => `/org/sgs/booking/${id}/confirm`,
  payBooking: (id: number) => `/org/sgs/booking/${id}/pay`,
  validateBagBarcode: (barcode: string) =>
    `/org/sgs/booking/validate-barcode?barcode=${barcode}`,
  addBagDetails: (id: number) => `/org/sgs/booking/${id}/bag-details`,
  getBagsEligibility: (id: number) => `/org/sgs/booking/${id}/bags/eligibility`,
  manageBags: (id: number) => `/org/sgs/booking/${id}/bags`,
};

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  private hubConnection: HubConnection;
  signalRBaseUrl = environment.signalRUrl;
  bookings: Booking[] = [];
  constructor(
    private httpClient: HttpClient,
    private credentialsService: CredentialsService,
  ) {}

  getBookings(context: BookingContext): Observable<BookingsListResponse> {
    // if (this.bookings?.length && context.statusIds === null) {
    //   return of(
    //     this.bookings.slice(
    //       (context.pageNumber - 1) * context.pageSize,
    //       context.pageNumber * context.pageSize
    //     )
    //   );
    // }
    return this.httpClient.get<BookingsListResponse>(
      routes.getBookings(context),
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
    // .pipe(
    //   tap((res: any) => {
    //     this.bookings = res.data; // Store all the records.
    //   }),
    //   map((res) => res.data),
    // );
  }

  cancelBooking(
    bookingId: number,
    data: Record<string, unknown>,
  ): Observable<Booking> {
    return this.httpClient.post<Booking>(
      routes.cancelBooking(bookingId),
      data,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  getBookingDetails(bookingId: number): Observable<Booking> {
    return this.httpClient.get<Booking>(routes.getBookingDetails(bookingId), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  getFlights(code: string, date: string): Observable<FlightSearchResult[]> {
    return this.httpClient.get<FlightSearchResult[]>(
      routes.getFlights(code, date),
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  getBookingAvailability(): Observable<BookingAvailability> {
    return this.httpClient.get<BookingAvailability>(
      routes.getBookingAvailability(),
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  timeSlots(data: TimeSlotsRequest): Observable<TimeSlotsResponse> {
    return this.httpClient.post<TimeSlotsResponse>(routes.timeSlots(), data, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  createPreBooking(data: Record<string, unknown>): Observable<unknown> {
    return this.httpClient.post(routes.createPreBooking(), data, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  getBookingPrices(
    id: number,
  ): Observable<{ paymentMethods: paymentMethod[] }> {
    return this.httpClient.post<{ paymentMethods: paymentMethod[] }>(
      routes.getBookingPrices(id),
      null,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  computeBagsSpecs(data: BagsSpecsRequest): Observable<bagSpecs> {
    return this.httpClient.post<bagSpecs>(routes.computeBagsSpecs(), data, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  computeBagsPricing(data: Record<string, unknown>): Observable<BagsPricing> {
    return this.httpClient.post<BagsPricing>(
      routes.computeBagsPricing(),
      data,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  createBooking(data: Record<string, unknown>): Observable<unknown> {
    return this.httpClient.post(routes.createBooking(), data, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  createBookingDropoffWindow(
    id: number,
    data: Record<string, unknown> = {},
  ): Observable<SlotWindowsResponse> {
    return this.httpClient.post<SlotWindowsResponse>(
      routes.createBookingDropoffWindow(id),
      data,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  updateBookingDropoffWindow(
    id: number,
    data: Partial<TimeWindow> & { isReselect: boolean },
  ): Observable<TimeWindow> {
    return this.httpClient.put<TimeWindow>(
      routes.updateBookingDropoffWindow(id),
      data,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  getStatusesByBookingGroupId(groupId: number) {
    let statuses;
    if (groupId !== 0) {
      statuses = Object.entries(statusesList).filter(
        (status) => status[1].groupId === groupId,
      );
    } else {
      statuses = Object.entries(statusesList);
    }
    return Object.keys(
      statuses.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
    ).join(',');
  }

  getSecurityCheck(airlineId?: number) {
    return this.httpClient.get<SecurityCheckQuestions>(
      routes.getSecurityCheck(),
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
        ...(airlineId != null && { params: { airlineId } }),
      },
    );
  }

  storeSecurityCheck(
    data: SecurityCheckAnswer[],
    airlineId?: number,
  ): Observable<{ securityCheckId: number }> {
    return this.httpClient.put<{ securityCheckId: number }>(
      routes.storeSecurityCheck(),
      data,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
        ...(airlineId != null && { params: { airlineId } }),
      },
    );
  }

  getBookingService(id: number) {
    return this.httpClient.get<ChangeServiceOption[]>(
      routes.getBookingService(id),
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  createBookingPickupWindow(
    id: number,
    data: Record<string, unknown> = {},
  ): Observable<SlotWindowsResponse> {
    return this.httpClient.post<SlotWindowsResponse>(
      routes.createBookingPickupWindow(id),
      data,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  updateBookingPickupWindow(
    id: number,
    data: Partial<TimeWindow> & { isReselect: boolean },
  ): Observable<TimeWindow> {
    return this.httpClient.put<TimeWindow>(
      routes.updateBookingPickupWindow(id),
      data,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  getCancelBookingOptions(id: number) {
    return this.httpClient.get<CancelBookingOption[]>(
      routes.getCancelBookingOptions(id),
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  changeBookingService(id: number, data: Record<string, unknown>) {
    return this.httpClient.post<ChangeServicePricing>(
      routes.changeBookingService(id),
      data,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  /**
   *
   * @param isSmsLink true if the builder been called by LBL sms link o
   * @returns
   */
  LiveTrackingConnectionBuilder(
    isSmsLinkCall: boolean,
  ): Promise<LiveTrackingConnectionResult> {
    const options: signalR.IHttpConnectionOptions = {
      accessTokenFactory: () => {
        return isSmsLinkCall
          ? JSON.parse(localStorage?.getItem('sms-token'))?.token
          : this.credentialsService.credentials.token;
      },
    };
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(
        routes.getLiveTrackingConnectionBuilderBaseUrl(this.signalRBaseUrl),
        options,
      )
      .build();

    return this.hubConnection
      .start()
      .then(() => {
        return { response: this.hubConnection, connected: true };
      })
      .catch((err: unknown) => {
        return { response: err, connected: false };
      });
  }

  initiateClickToCall(id: number) {
    return this.httpClient.post(
      routes.initiateClickToCall(id),
      {},
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  getListOfFeedbackIssues(id: number): Observable<Issue[]> {
    return this.httpClient.get<Issue[]>(routes.getListOfFeedbackIssues(id), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  uploadBagImg(body: FormData) {
    return this.httpClient.post(routes.uploadBagImg(), body, {
      headers: new HttpHeaders({
        'x-ms-blob-type': 'BlockBlob',
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  postBookingFeedback(
    id: number,
    body: Record<string, unknown>,
  ): Observable<unknown> {
    return this.httpClient.post(routes.postBookingFeedback(id), body, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  confirmBooking(
    id: number,
    body: Record<string, unknown>,
  ): Observable<unknown> {
    return this.httpClient.put(routes.confirmBooking(id), body, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  confirmChangeService(
    id: number,
    body: Record<string, unknown>,
  ): Observable<ChangeServiceConfirmResult> {
    return this.httpClient.post<ChangeServiceConfirmResult>(
      routes.confirmChangeService(id),
      body,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }

  confirmPayment(
    id: number,
    body: Record<string, unknown>,
    airlineId?: number,
  ): Observable<ConfirmPaymentResult> {
    return this.httpClient.put<ConfirmPaymentResult>(
      routes.confirmPayment(id),
      body,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
        ...(airlineId != null && { params: { airlineId } }),
      },
    );
  }
  payBooking(
    id: number,
    body: Record<string, unknown>,
  ): Observable<PayBookingResult> {
    return this.httpClient.post<PayBookingResult>(routes.payBooking(id), body, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }
  validateBagBarcode(barcode: string): Observable<unknown> {
    return this.httpClient.get(routes.validateBagBarcode(barcode), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  addBagDetails(id: number, body: unknown): Observable<unknown> {
    return this.httpClient.post(routes.addBagDetails(id), body, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  getBagsEligibility(id: number): Observable<BagsEligibility> {
    return this.httpClient.get<BagsEligibility>(routes.getBagsEligibility(id), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
      }),
    });
  }

  manageBags(
    id: number,
    body: ManageBagsRequest,
  ): Observable<ManageBagsResponse> {
    return this.httpClient.post<ManageBagsResponse>(
      routes.manageBags(id),
      body,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        }),
      },
    );
  }
}
